import { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';

const PAD = 8;

export function ProductTour({ run, steps, onClose, onComplete }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const step = steps[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useLayoutEffect(() => {
    if (!run) return;
    measure();
  }, [run, i, measure]);

  useEffect(() => {
    if (!run) return;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [run, measure]);

  useEffect(() => { if (run) setI(0); }, [run]);

  if (!run || !step) return null;

  const last = i === steps.length - 1;
  const finish = () => { onClose(); if (onComplete) onComplete(); };
  const close = () => onClose();

  // Tooltip position — to the right of the highlighted nav item, fallback to center.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const cardW = 300;
  let cardStyle;
  if (rect) {
    let left = rect.left + rect.width + 16;
    if (left + cardW > vw - 12) left = Math.max(12, rect.left - cardW - 16);
    let top = rect.top;
    if (top + 180 > vh) top = Math.max(12, vh - 200);
    cardStyle = { top, left };
  } else {
    cardStyle = { top: vh / 2 - 90, left: vw / 2 - cardW / 2 };
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid="product-tour">
      {/* Spotlight cutout via giant box-shadow */}
      <AnimatePresence>
        {rect ? (
          <motion.div
            key={`hole-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute rounded-xl pointer-events-none"
            style={{
              top: rect.top - PAD,
              left: rect.left - PAD,
              width: rect.width + PAD * 2,
              height: rect.height + PAD * 2,
              boxShadow: '0 0 0 9999px rgba(15,23,42,0.62)',
              transition: 'all 0.25s ease',
            }}
          />
        ) : (
          <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.62)' }} />
        )}
      </AnimatePresence>

      {/* Click-catcher to allow skipping by clicking the dimmed area */}
      <div className="absolute inset-0" onClick={close} />

      <AnimatePresence mode="wait">
        <motion.div
          key={`card-${i}`}
          initial={{ opacity: 0, scale: 0.96, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          className="absolute bg-white rounded-2xl shadow-2xl border border-slate-200 p-5"
          style={{ width: cardW, ...cardStyle }}
          data-testid="tour-card"
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={close} className="absolute top-3 right-3 text-slate-300 hover:text-slate-500"
            data-testid="tour-close-btn" aria-label="Close tour">
            <X className="w-4 h-4" />
          </button>
          <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-1">
            Step {i + 1} of {steps.length}
          </p>
          <h3 className="text-base font-semibold text-slate-900" data-testid="tour-title">{step.title}</h3>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{step.body}</p>

          <div className="flex items-center justify-between mt-4">
            {i > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setI(i - 1)}
                className="text-slate-500 h-8" data-testid="tour-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
            ) : (
              <button onClick={close} className="text-xs text-slate-400 hover:text-slate-600"
                data-testid="tour-skip-btn">Skip</button>
            )}
            <Button size="sm" onClick={() => (last ? finish() : setI(i + 1))}
              className="bg-slate-900 hover:bg-slate-800 text-white h-8" data-testid="tour-next-btn">
              {last ? 'Finish' : <>Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
}
